import PropTypes from "prop-types";
import UXloader from "../layouts/UXloader";
import ErrorPage from "../layouts/ErrorPage";
import { App, Typography } from "antd";
import { connect } from "react-redux";
import { PATHS } from "../../utills/paths";
import { useNavigate } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";
import { getProductList } from "../../utills/common";
import { YESNO, PLAN_FEE_TYPE, USER_ROLES, INVITE_TYPE, PLAN_RECURRING_TYPE } from "../../actions/constants";
import { createContext, useEffect, useMemo, useContext, useCallback } from "react";
import { resetState, getUserInfo, updateState, userActions, listCountry, updateWorkspace } from "../../actions/user";
import ManageSeatsModal from "../organization/layouts/ManageSeatsModal";

const { Link } = Typography;

// AuthContext to keep the latest state
const AuthContext = createContext(null);

// useAuthContext to share this components state everywhere this component is imported
export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) throw new Error("useAuthContext context must be use inside AuthProvider");

  return context;
};

const AuthProvider = ({
  auth,
  children,
  resetState,
  updateState,
  getUserInfo,
  listCountry,
  updateWorkspace,
  fetchBalances = false,
  fetchProjects = false,
  fetchBillings = false,
  fetchReqDetails = false,
}) => {
  const {
    user,
    error,
    access,
    isLoading,
    countries,
    billings,
    invoices,
    userAccess,
    usageHistory,
    subscription,
    openAiOptinPopup = false,
    openManageSeatsModal = false,
    creditPrices,
    allowedTestTypes,
    allowedFeatures,
    balances,
    projects = [],
    workspace,
    upcomingInvoice = null,
    organizationPeople = [],
    organizationPeopleInvited = [],
    isFetchingWorkspace = false,
    userPrevTaskUrlAllowed = false,
  } = auth;

  const navigate = useNavigate();
  const { notification } = App.useApp();
  const { isOrgAdminOrOwner = false } = userAccess || {};
  const { organization = {}, ai_summary_enabled = false } = workspace || {};
  const { address = "", paymentMethods = [], default_source } = billings || {};

  useIdleTimer({
    name: "idle-timer",
    crossTab: true,
    syncTimers: 500,
    startOnMount: true,
    eventsThrottle: 200,
    leaderElection: true,
    timeout: 1000 * 60 * 2, // user will considered Idle in case of user in-activity for two minutes.
    onPresenceChange: (presence) => {
      if (presence?.type === "active") getUserInfo(false, fetchReqDetails, fetchBalances, fetchProjects, fetchBillings);
    },
  });

  useEffect(() => {
    getUserInfo(true, fetchReqDetails, fetchBalances, fetchProjects, fetchBillings);
  }, [getUserInfo, fetchReqDetails, fetchBalances, fetchProjects, fetchBillings]);

  // Fetch Countries if not available
  useEffect(() => {
    if (countries?.length === 0) listCountry();
  }, [countries, listCountry]);

  // Default Workspace ID
  const workspaceId = useMemo(() => workspace?.id || "", [workspace?.id]);

  // Products
  const products = useMemo(() => getProductList(subscription?.plan), [subscription]);

  // OPT_IN for AI
  const isOptInForAI = useMemo(() => Number(ai_summary_enabled) === YESNO.YES, [ai_summary_enabled]);

  // Root Project ID - Phase 0
  const rootProjectId = useMemo(() => projects?.find((project) => project?.is_root)?.id, [projects]);

  // Update AI Opt In
  const updateAIOptIn = useCallback(
    (isOptIn, openAiOptinPopup = false) => updateState({ openAiOptinPopup, workspace: { ...workspace, ai_summary_enabled: isOptIn } }),
    [updateState, workspace]
  );

  // Plan Info
  const planInfo = useMemo(() => {
    const { cancel_at = null, plan = { fee_type: PLAN_FEE_TYPE.FREE } } = subscription || {};
    const { credits_panel = 0, credits_response_total = 0, teamManagement } = organization || {};

    const totalResponseCredits = Number(plan?.own_users) || 0;
    const isPaidPlan = Number(plan?.fee_type) === PLAN_FEE_TYPE.PAID || false;

    const { credits } = user;
    const {
      activeTestCounts: {
        task: TBTActiveUATests = [],
        survey: SurveyActiveUATests = [],
        noRecording: NoRecordingActiveUATests = [],
      } = {},
    } = balances || {};

    const pendingUAsTBT = (TBTActiveUATests || []).reduce((sum, test) => {
      return sum + (test.CountUARequired - test.CountUACompleted);
    }, 0);
    const pendingUAsSurvey = (SurveyActiveUATests || []).reduce((sum, test) => {
      return sum + (test.CountUARequired - test.CountUACompleted);
    }, 0);
    const pendingUAsNoRecording = (NoRecordingActiveUATests || []).reduce((sum, test) => {
      return sum + (test.CountUARequired - test.CountUACompleted);
    }, 0);
    const pendingResponseUACreditsTBT = Number(pendingUAsTBT) * Number(credits?.task?.uxarmy || 0);
    const pendingResponseUACreditsSurvey = Number(pendingUAsSurvey) * Number(credits?.survey?.uxarmy || 0);
    const pendingResponseUACreditsNoRecording = Number(pendingUAsNoRecording) * Number(credits?.noRecording?.uxarmy || 0);
    const pendingResponseUACredits =
      pendingResponseUACreditsTBT + pendingResponseUACreditsSurvey + pendingResponseUACreditsNoRecording;

    const insufficientUACredits = pendingResponseUACredits - Number(credits_panel);

    return {
      plan,
      balances,
      isPaidPlan,
      subscription,
      upcomingInvoice,
      totalResponseCredits,
      insufficientUACredits,
      isPlanCancelled: !!cancel_at, // is plan cancelled
      creditsPanel: Number(credits_panel),
      creditsResponseTotal: Number(credits_response_total),
      isProPlan: isPaidPlan && teamManagement?.enabled,
      isFreePlan: Number(plan?.fee_type) === PLAN_FEE_TYPE.FREE || false,
      isCustomPlan: Number(plan?.fee_type) === PLAN_FEE_TYPE.MANUAL || false,
      isMonthlyPlan: plan?.product?.recurring_type === PLAN_RECURRING_TYPE.MONTH,
    };
  }, [balances, organization, subscription, user, upcomingInvoice]);

  // Default Address
  const defaultAddress = useMemo(() => {
    return [address?.line1, address?.line2, address?.city, address?.state, address?.country, address?.postal_code]
      .map((v) => v?.trim())
      ?.filter(Boolean);
  }, [address]);

  // Has Payment Methods
  const hasPMs = useMemo(() => paymentMethods?.length > 0, [paymentMethods]);

  // Default Payment Method
  const defaultPM = useMemo(() => paymentMethods?.find((pm) => pm?.id === default_source), [paymentMethods, default_source]);

  // Has Address
  const hasAddress = useMemo(() => {
    if (!address) return false;

    const { line1, country } = address;
    return [line1, country].every((value) => value?.trim()?.length > 0);
  }, [address]);

  // Check if a user is allowed to perform an action based on their role
  const isRoleActionAllowed = useCallback(
    (role = "", resourceType = "", showNotification = true, currentRole = "") => {
      const { members = [], orgMembers = [], organization } = workspace || {};
      const { teamManagement, seats, collaborators } = organization || {};
      if (!teamManagement?.enabled || role === "") {
        notification.warning({
          description: (
            <div className='pr-1'>
              Your plan does not support Team Management.
              <br />
              {isOrgAdminOrOwner && (
                <span className='text-underline-offset text-white pointer' onClick={() => navigate(PATHS.V2.PLANS)}>
                  Upgrade Plan
                </span>
              )}
            </div>
          ),
        });
        return false; // Team Management is not enabled or role is not provided
      }

      const isCurrentRoleEditor = currentRole === USER_ROLES.WRITE || currentRole === USER_ROLES.ADMIN;
      const availableSeat = seats?.allowed - seats?.occupied + (isCurrentRoleEditor ? 1 : 0);
      const availableCollab = collaborators?.allowed - collaborators?.occupied;
      const maxOrgAdminCount = teamManagement?.limits?.organizationAdmins?.max || 0;
      const maxWorkspaceAdminCount = teamManagement?.limits?.workspaceAdmins?.max || 0;

      // Current Organization/Workspace Admin Count
      const currentOrgAdminCount =
        orgMembers?.filter((item) => Number(item?.is_dormant) === YESNO.NO && item?.role === USER_ROLES.ADMIN)?.length || 0;
      const currentWorkspaceAdminCount =
        members?.filter((item) => Number(item?.is_dormant) === YESNO.NO && item?.role === USER_ROLES.ADMIN)?.length || 0;

      // Access for Admin
      const isAdminRole = role === USER_ROLES.ADMIN;
      const isWorkspaceResource = resourceType === INVITE_TYPE.WORKSPACE;
      const isOrganizationResource = resourceType === INVITE_TYPE.ORGANIZATION;

      // Response
      let message = "";
      let response = true;

      if ((role === USER_ROLES.WRITE || isAdminRole) && availableSeat <= 0) {
        response = false;
        const allUsed = seats?.occupied === seats?.max;

        message = (
          <div className='pr-1'>
            {allUsed
              ? `You have reached maximum limit of ${seats?.allowed} editor${seats?.allowed > 1 ? "s" : ""} in your plan.`
              : "You will need to purchase additional seats to make someone an editor"}

            {isOrgAdminOrOwner && !planInfo?.isCustomPlan && (
              <>
                <br />

                {allUsed && (
                  <Link href={PATHS.CONTACT_SALES} target='_blank' className='text-underline-offset text-white'>
                    Upgrade to Enterprise Plan
                  </Link>
                )}

                {!allUsed && (
                  <span
                    className='text-underline-offset text-white pointer'
                    onClick={() => updateState({ openManageSeatsModal: true })}
                  >
                    Add Seats
                  </span>
                )}
              </>
            )}
          </div>
        );
      }

      if (isAdminRole && isOrganizationResource && maxOrgAdminCount - currentOrgAdminCount <= 0) {
        response = false;
        message = `${maxOrgAdminCount} ${
          maxOrgAdminCount > 1 ? "members are" : "member is"
        } currently an organisation admin. Please change it to an editor/collaborator before assigning a new organization admin.`;
      }

      if (isAdminRole && isWorkspaceResource && maxWorkspaceAdminCount - currentWorkspaceAdminCount <= 0) {
        response = false;
        message = `${maxWorkspaceAdminCount}  ${
          maxWorkspaceAdminCount > 1 ? "members are" : "member is"
        } currently a workspace admin. Please change it to an editor/collaborator before assigning a new workspace admin.`;
      }

      if (role === USER_ROLES.COLLABORATOR) {
        if (availableCollab <= 0 && !planInfo?.isCustomPlan) {
          response = false;
          message = (
            <div className='pr-1'>
              You have reached maximum limit of {collaborators?.allowed} collaborators
              {isOrgAdminOrOwner && (
                <>
                  <br />
                  <span className='text-underline-offset text-white pointer' onClick={() => navigate(PATHS.V2.PLANS)}>
                    Upgrade Plan
                  </span>
                </>
              )}
            </div>
          );
        } else if (availableCollab <= 0 && planInfo?.isCustomPlan) {
          response = false;
          message = `You have reached maximum limit of ${collaborators?.allowed} collaborators. Please remove before adding new collaborator.`;
        }
      }

      // Show Notification
      if (message && showNotification) notification.warning({ description: message });

      return response;
    },
    [workspace, planInfo?.isCustomPlan, notification, isOrgAdminOrOwner, navigate, updateState]
  );

  // console.log("Auth Provider", { ...auth, planInfo });

  const memoizedValue = useMemo(
    () => ({
      user,
      access,
      countries,
      isLoading,
      resetState,
      getUserInfo,
      products,
      billings,
      planInfo,
      invoices,
      projects,
      usageHistory,
      updateState,
      isOptInForAI,
      userActions,
      organization,
      creditPrices,
      allowedTestTypes,
      allowedFeatures,
      subscription,
      updateAIOptIn,
      defaultAddress,
      openAiOptinPopup,
      updateUserState: updateState,
      hasAddress,
      hasPMs,
      defaultPM,
      rootProjectId,
      workspace,
      workspaceId,
      userAccess,
      organizationPeople,
      isRoleActionAllowed,
      isFetchingWorkspace,
      userPrevTaskUrlAllowed,
      organizationPeopleInvited,
      updateWorkspace,
    }),
    [
      user,
      access,
      countries,
      isLoading,
      resetState,
      getUserInfo,
      products,
      billings,
      planInfo,
      invoices,
      projects,
      usageHistory,
      updateState,
      organization,
      creditPrices,
      allowedTestTypes,
      allowedFeatures,
      isOptInForAI,
      updateAIOptIn,
      subscription,
      defaultAddress,
      openAiOptinPopup,
      hasAddress,
      hasPMs,
      defaultPM,
      rootProjectId,
      workspace,
      workspaceId,
      userAccess,
      organizationPeople,
      isRoleActionAllowed,
      isFetchingWorkspace,
      userPrevTaskUrlAllowed,
      organizationPeopleInvited,
      updateWorkspace,
    ]
  );

  if (error) return <ErrorPage error={error} />;

  if (isLoading) {
    return (
      <div className='position-fixed w-100 zi-999 overflow-hidden'>
        <UXloader />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}

      <ManageSeatsModal open={openManageSeatsModal} />
    </AuthContext.Provider>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });
const actionFuncs = { resetState, getUserInfo, updateState, listCountry, updateWorkspace };

AuthProvider.propTypes = {
  auth: PropTypes.object,
  children: PropTypes.node,
  resetState: PropTypes.func,
  updateState: PropTypes.func,
  getUserInfo: PropTypes.func,
  listCountry: PropTypes.func,
  updateWorkspace: PropTypes.func,
};

export default connect(mapStateToProps, actionFuncs)(AuthProvider);
